import React, { useEffect, useMemo, useState } from "react";
import { Check, Clock, CreditCard, X } from "lucide-react";
import MainLayout from "../components/CommonComponents/MainLayout";
import EmptyState from "../components/CommonComponents/EmptyState";
import Pagination from "../components/CommonComponents/Pagination";
import SearchBar from "../components/CommonComponents/SearchInput";
import ConfirmationModal from "../components/CommonComponents/ConfirmationModal";
import { tableBase, tableCell, tableContainer, tableHead, tableHeader, tableRow } from "../utils/tableStyle";
import { fetchAddonRequests, updateAddonRequestStatus } from "../services/addonRequestApi";
import { showErrorToast, showSuccessToast } from "../utils/toast";

const statusStyles = {
  PENDING: "bg-amber-100 text-amber-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
  DENIED: "bg-rose-100 text-rose-700",
};

const formatCurrency = (value) => `₱${Number(value || 0).toLocaleString()}`;
const formatDate = (value) => value ? new Date(value).toLocaleString() : "-";

export default function AddonRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewingId, setReviewingId] = useState(null);
  const [confirmReview, setConfirmReview] = useState({ open: false, request: null, status: null });
  const perPage = 6;

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await fetchAddonRequests();
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load add-on requests:", error);
      showErrorToast("Failed to load add-on requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return requests;
    return requests.filter((request) => {
      const customerName = request.customer?.name || "";
      const requester = request.requestedBy?.username || request.requestedBy?.email || "";
      return `${customerName} ${requester} ${request.status}`.toLowerCase().includes(term);
    });
  }, [requests, searchTerm]);

  const totalPages = Math.max(Math.ceil(filteredRequests.length / perPage), 1);
  const indexOfFirst = (currentPage - 1) * perPage;
  const currentRequests = filteredRequests.slice(indexOfFirst, indexOfFirst + perPage);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const handleReview = async (request, status) => {
    setReviewingId(request.id);
    try {
      await updateAddonRequestStatus(request.id, status);
      showSuccessToast(`Request ${status.toLowerCase()}.`);
      await loadRequests();
    } catch (error) {
      console.error("Failed to review add-on request:", error);
      showErrorToast(error.response?.data?.message || "Failed to review add-on request.");
    } finally {
      setReviewingId(null);
    }
  };

  const openReviewConfirm = (request, status) => {
    setConfirmReview({ open: true, request, status });
  };

  const closeReviewConfirm = () => {
    if (reviewingId) return;
    setConfirmReview({ open: false, request: null, status: null });
  };

  const confirmReviewAction = async () => {
    const { request, status } = confirmReview;
    if (!request || !status) return;
    await handleReview(request, status);
    setConfirmReview({ open: false, request: null, status: null });
  };

  const pendingCount = requests.filter((request) => request.status === "PENDING").length;
  const approvedCount = requests.filter((request) => request.status === "APPROVED").length;

  return (
    <MainLayout>
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-6 px-4 py-8">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                <CreditCard className="h-4 w-4" />
                Add-on Requests
              </div>
              <h1 className="mt-4 text-3xl font-bold text-slate-950">Customer Add-ons</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-500">
                Review extra user allowance requests from tenant admins. Approved requests increase the customer staff limit immediately.
              </p>
            </div>
            <div className="grid min-w-[260px] grid-cols-2 gap-3 text-center">
              <div className="rounded-xl bg-amber-50 px-4 py-3">
                <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
                <p className="text-xs font-semibold uppercase text-amber-600">Pending</p>
              </div>
              <div className="rounded-xl bg-emerald-50 px-4 py-3">
                <p className="text-2xl font-bold text-emerald-700">{approvedCount}</p>
                <p className="text-xs font-semibold uppercase text-emerald-600">Approved</p>
              </div>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchBar
            placeholder="Search customer or requester..."
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setCurrentPage(1);
            }}
            disabled={loading}
          />
          <button
            type="button"
            onClick={loadRequests}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>

        {loading && requests.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
            Loading add-on requests...
          </div>
        ) : filteredRequests.length === 0 ? (
          <EmptyState
            icon={Clock}
            title="No Add-on Requests"
            message="Customer add-on requests will appear here after an admin submits one."
          />
        ) : (
          <>
            <div className={tableContainer}>
              <table className={tableBase}>
                <thead className={tableHead}>
                  <tr>
                    <th className={`${tableHeader} text-left`}>Customer</th>
                    <th className={`${tableHeader} text-left`}>Requested By</th>
                    <th className={`${tableHeader} text-center`}>Users</th>
                    <th className={`${tableHeader} text-left`}>Billing</th>
                    <th className={`${tableHeader} text-left`}>Status</th>
                    <th className={`${tableHeader} text-left`}>Requested</th>
                    <th className={`${tableHeader} text-center`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRequests.map((request) => (
                    <tr key={request.id} className={`${tableRow} border-b last:border-0`}>
                      <td className={`${tableCell} font-semibold`}>
                        {request.customer?.name || `Customer #${request.customer_id}`}
                      </td>
                      <td className={tableCell}>
                        <div className="font-medium text-slate-900">{request.requestedBy?.username || "-"}</div>
                        <div className="text-xs text-slate-500">{request.requestedBy?.email || "-"}</div>
                      </td>
                      <td className={`${tableCell} text-center font-semibold`}>{request.quantity}</td>
                      <td className={tableCell}>
                        <div className="text-sm font-semibold text-slate-900">{formatCurrency(request.new_monthly_amount)}/mo</div>
                        <div className="text-xs text-slate-500">Base {formatCurrency(request.base_monthly_amount)} + add-on {formatCurrency(request.addon_monthly_amount)}</div>
                      </td>
                      <td className={tableCell}>
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[request.status] || "bg-slate-100 text-slate-600"}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className={tableCell}>{formatDate(request.createdAt || request.created_at)}</td>
                      <td className={`${tableCell} text-center`}>
                        {request.status === "PENDING" ? (
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openReviewConfirm(request, "APPROVED")}
                              disabled={reviewingId === request.id}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition hover:bg-emerald-100 disabled:opacity-60"
                              title="Approve request"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openReviewConfirm(request, "DENIED")}
                              disabled={reviewingId === request.id}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-700 shadow-sm transition hover:bg-rose-100 disabled:opacity-60"
                              title="Deny request"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Reviewed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              <p className="text-sm text-slate-500">
                Showing {indexOfFirst + 1} - {Math.min(indexOfFirst + perPage, filteredRequests.length)} of {filteredRequests.length} requests
              </p>
            </div>
          </>
        )}
        <ConfirmationModal
          isOpen={confirmReview.open}
          onClose={closeReviewConfirm}
          onConfirm={confirmReviewAction}
          title={confirmReview.status === "APPROVED" ? "Approve Add-on Request" : "Decline Add-on Request"}
          message={
            confirmReview.request
              ? `${confirmReview.status === "APPROVED" ? "Approve" : "Decline"} ${confirmReview.request.quantity} additional user slot${Number(confirmReview.request.quantity) === 1 ? "" : "s"} for ${confirmReview.request.customer?.name || `Customer #${confirmReview.request.customer_id}`}?`
              : ""
          }
          confirmText={reviewingId ? "Processing..." : confirmReview.status === "APPROVED" ? "Approve" : "Decline"}
          cancelText="Cancel"
          type={confirmReview.status === "DENIED" ? "delete" : "default"}
        />
      </div>
    </MainLayout>
  );
}
